import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPublicUser } from '../lib/api';
import { useT } from '../lib/i18n';
import { ReliabilityBadge } from '../components/ReliabilityBadge';
import { BackButton } from '../lib/BackButton';
import { Card, CardHint, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { VibesBadges } from '../components/VibesCard';
import { RetryCard } from '../components/RetryCard';

export function PublicProfilePage({ userId }: { userId: string }): JSX.Element {
  const { t } = useT();
  const [explain, setExplain] = useState(false);
  const q = useQuery({ queryKey: ['user', userId], queryFn: () => getPublicUser(userId) });

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />
      {q.isLoading && (
        <div className="space-y-3">
          <div className="h-20 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-32 animate-pulse rounded-card bg-tg-bg-secondary" />
        </div>
      )}
      {q.isError && (
        <RetryCard message={(q.error as Error).message} onRetry={() => q.refetch()} isRetrying={q.isFetching} />
      )}
      {q.data && (
        <>
          <header className="mb-4 flex items-center gap-3">
            {q.data.avatarUrl ? (
              <img src={q.data.avatarUrl} alt="" className="h-14 w-14 rounded-full" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-navy text-brand-cream">
                {q.data.displayName.slice(0, 1)}
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-xl font-bold text-tg-text">{q.data.displayName}</h1>
              <p className="text-xs text-tg-hint">
                {q.data.isDriver && t(q.data.tierLabelKey as 'tier.newcomer')}
                {q.data.isDriver && ' · '}
                {t('profile.ridesCompleted', { n: q.data.ridesCompleted })}
                {q.data.ratingAvg !== null &&
                  ` · ★ ${q.data.ratingAvg.toFixed(1)} (${q.data.ratingCount})`}
              </p>
              <p className="mt-0.5 text-xs text-tg-hint">
                {t('profile.memberSince', {
                  date: new Date(q.data.memberSince).toLocaleDateString(undefined, {
                    month: 'short',
                    year: 'numeric',
                  }),
                })}
              </p>
            </div>
          </header>
          {q.data.badges.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {q.data.badges.map((b) => (
                <Badge key={b} tone="success">
                  {t(`badge.${b}` as 'badge.founding_driver')}
                </Badge>
              ))}
            </div>
          )}
          {q.data.isDriver && (
            <Card className="mb-3 space-y-1">
              <div className="flex items-center justify-between">
                <CardTitle>{t('reliability.label')}</CardTitle>
                <ReliabilityBadge
                  score={q.data.reliability}
                  ridesCompleted={q.data.ridesCompleted}
                />
              </div>
              <p className="text-sm font-semibold text-tg-text">{q.data.reliability}/100</p>
              <button
                type="button"
                onClick={() => setExplain((v) => !v)}
                className="text-left text-xs text-tg-link underline"
              >
                {t('reliability.whatis.q')}
              </button>
              {explain && <CardHint>{t('reliability.whatis.a')}</CardHint>}
            </Card>
          )}
          {q.data.bio && (
            <Card>
              <CardTitle>{t('public.about')}</CardTitle>
              <CardHint>{q.data.bio}</CardHint>
            </Card>
          )}
          {q.data.vibes && (
            <Card className="mt-3 space-y-2">
              <CardTitle>{t('vibes.title')}</CardTitle>
              <VibesBadges vibes={q.data.vibes} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
